import { ReactNode, createContext, useContext, useMemo, useState, useEffect } from 'react';
import { INotificationViewModel } from 'viewModels';
import { useInfiniteQuery } from 'react-query';
import { NotificationController } from '../controller/NotificationController';
import useWebSocket from '../hooks/useWebSocket';
import { useAuth } from './auth';
import { NotificationType } from '../types/enums';

type NotificationContextProps = {
  children: ReactNode;
};

interface IInitialValues {
  notifications: INotificationViewModel[];
  setNotifications: React.Dispatch<React.SetStateAction<INotificationViewModel[]>>;
  enableFetch: boolean;
  setEnableFetch: React.Dispatch<React.SetStateAction<boolean>>;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
}

const NotificationContext = createContext({} as IInitialValues);

const NotificationContextProvider = ({ children }: NotificationContextProps) => {
  const { user, tokenIsValid } = useAuth();
  const [enableFetch, setEnableFetch] = useState(false);
  const [notifications, setNotifications] = useState<INotificationViewModel[]>([]);
  const PAGE_SIZE = 5;

  const { fetchNextPage, hasNextPage } = useInfiniteQuery(
    ['notifications'],
    ({ pageParam = 1 }) => NotificationController.getByUserId(user.id, pageParam, PAGE_SIZE),
    {
      onSuccess: (data) => {
        setNotifications((prevNotifications) => {
          const newNotifications = data.pages
            .flatMap((notificationsPage) =>
              notificationsPage.map((n) => {
                const newNotification = { ...n };
                if (newNotification.metadataStr) {
                  const metadata = JSON.parse(n.metadataStr);
                  if (
                    newNotification.type === NotificationType.NEW_DEMAND ||
                    newNotification.type === NotificationType.NEW_SOLICITATION ||
                    newNotification.type === NotificationType.DEMAND_COUNTER_PROPOSAL ||
                    newNotification.type === NotificationType.SOLICITATION_CONCLUSION
                  ) {
                    newNotification.serviceId = metadata?.serviceId;
                  }
                  if (newNotification.type === NotificationType.NEW_MESSAGE) {
                    newNotification.conversationId = metadata?.conversationId;
                  }
                  if (newNotification.type === NotificationType.REPORT_READY) {
                    newNotification.reportId = metadata?.reportId;
                  }
                }
                return newNotification;
              }),
            )
            .filter((n) => !prevNotifications.some((p) => p.id === n.id));
          return [...prevNotifications, ...newNotifications].sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          );
        });
      },
      getNextPageParam: (lastPage, allPages) => {
        const nextPage = allPages.length + 1;
        return lastPage.length < PAGE_SIZE ? undefined : nextPage;
      },
      enabled: enableFetch && !!user.id && tokenIsValid,
      refetchOnWindowFocus: false,
    },
  );

  useWebSocket<INotificationViewModel>({
    url: `notification/${user?.id}`,

    onMessage: (notification) => {
      const formatedNotification = { ...notification };
      if (formatedNotification.metadataStr) {
        const metadata = JSON.parse(notification.metadataStr);
        if (
          notification.type === NotificationType.NEW_DEMAND ||
          notification.type === NotificationType.NEW_SOLICITATION ||
          notification.type === NotificationType.DEMAND_COUNTER_PROPOSAL ||
          notification.type === NotificationType.SOLICITATION_CONCLUSION
        ) {
          formatedNotification.serviceId = metadata?.serviceId;
        }
        if (notification.type === NotificationType.NEW_MESSAGE) {
          formatedNotification.conversationId = metadata?.conversationId;
        }
        if (notification.type === NotificationType.REPORT_READY) {
          formatedNotification.reportId = metadata?.reportId;
        }
      }
      setNotifications((prev) =>
        [formatedNotification, ...prev].sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        ),
      );
    },
  });

  useEffect(() => {
    if (!tokenIsValid) {
      setNotifications([]);
    }
  }, [tokenIsValid]);

  const contextValues = useMemo(
    () => ({
      notifications,
      setNotifications,
      fetchNextPage,
      hasNextPage,
      enableFetch,
      setEnableFetch,
    }),
    [enableFetch, fetchNextPage, hasNextPage, notifications],
  );

  return (
    <NotificationContext.Provider value={contextValues}>{children}</NotificationContext.Provider>
  );
};

export const useNotification = () => useContext(NotificationContext);
export default NotificationContextProvider;
